// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDeleteItemPopup } from './use-delete-item-popup';
import {
  selectWorkflowState,
  setWorkflow,
} from '@advisor-app/store/slices/workflow';
import { useSelectUniverse } from '@advisor-app/hooks';
import { type BasePortfolio } from '@shared/data-access/interfaces/common/base-portfolio';
import { useAppSelector } from '@advisor-app/store/store';
import {
  removeSavedPortfolio,
  selectIdeasTableData,
} from '@advisor-app/store/slices/my-research';
import { type WorkflowState } from '@shared/data-access/interfaces/redux/workflow-state';
import { type SavingHolding } from '@shared/data-access/interfaces/api/engine/citec';
import { type MyIdeas } from '@shared/data-access/interfaces/common/my-ideas';
import { type SubRows } from '@shared/data-access/interfaces/common/my-investment';
import {
  type SavedPortfolioRows,
  type SavedPortfolioSubRows,
} from '@shared/data-access/interfaces/redux/idea-state';
import { useTranslation } from 'react-i18next';

export interface LoadIdeaOnReduxProps {
  row: any;
  flow: WorkflowState;
  benchmark: string;
  portfolioName: string;
}

export interface EditPortfolioConfig {
  portfolioId: number;
  portfolioName: string;
  portfolioBenchmark: string;
  portfolioDate: string;
  portfolioHoldings: SavingHolding[];
}

type RowData = MyIdeas | SubRows;

export const useHandlerMyIdeasTable = () => {
  const { t } = useTranslation();
  const workflow = useAppSelector(selectWorkflowState);
  const [portfolioName, setPortfolioName] = useState('');
  const [isEditPortfolio, setIsEditPortfolio] = useState(false);
  const [dataPortfolio, setDataPortfolio] = useState<BasePortfolio>({
    ticker: [],
    date: [],
    unrealized_capital_gains: [],
    weight: [],
  });

  const initialEditPortfolioConfig: EditPortfolioConfig = useMemo(
    () => ({
      portfolioId: 0,
      portfolioName: '',
      portfolioBenchmark: '',
      portfolioDate: '',
      portfolioHoldings: [],
    }),
    [],
  );

  const [editPortfolioConfig, setEditPortfolioConfig] =
    useState<EditPortfolioConfig>(initialEditPortfolioConfig);

  const [openModalCash, setOpenModalCash] = useState(false);
  const [openModalPortfolio, setOpenModalPortfolio] = useState(false);

  const dataIdeasTable = useAppSelector(selectIdeasTableData);

  const { handleSelectBenchmark } = useSelectUniverse('ai-builder');

  const { deleteItemPopup } = useDeleteItemPopup();

  const dispatch = useDispatch();

  const loadIdeaOnRedux = ({
    row,
    flow,
    benchmark,
    portfolioName,
  }: LoadIdeaOnReduxProps) => {
    setOpenModalCash(true);
    dispatch(setWorkflow({ ...flow }));

    const ticker: string[] = [];
    const weight: number[] = [];
    const unrealized_capital_gains: number[] = [];
    const date: string[] = [];

    for (let i = 0; i < row.length; i++) {
      ticker.push(row[i].original.columnName.ticker);
      weight.push(Number(row[i].original.weight));
      unrealized_capital_gains.push(
        Number(row[i].original.unrealized_capital_gains),
      );
      date.push(row[i].original.creation_date);
    }

    setDataPortfolio({
      ticker,
      weight,
      unrealized_capital_gains,
      date,
    });

    setPortfolioName(portfolioName);

    handleSelectBenchmark(benchmark);
  };

  const handleEditPortfolio = (row: RowData) => {
    setIsEditPortfolio(true);
    setOpenModalPortfolio(true);

    const portfolioHoldings: SavingHolding[] = [];

    const {
      portfolio_id: portfolioId,
      portfolio: portfolioName,
      benchmark: portfolioBenchmark,
      creation_date: portfolioDate,
    } = row as SavedPortfolioRows;

    row.subRows.forEach((subRow: SavedPortfolioSubRows) => {
      portfolioHoldings.push({
        ticker: subRow.portfolio.ticker,
        weight: subRow.portfolio.weight * 100,
        unrealized_capital_gains: subRow.portfolio.unrealized_capital_gains,
      });
    });

    setPortfolioName(portfolioName);

    setEditPortfolioConfig({
      portfolioId,
      portfolioName,
      portfolioBenchmark,
      portfolioDate,
      portfolioHoldings,
    });
  };

  const handleDeleteIdea = useCallback(
    (portfolio_id: number, name: string) => {
      deleteItemPopup(
        {
          title: t('research.delete-portfolio.title', { ns: 'research' }),
          text: t('research.delete-portfolio.text', { name, ns: 'research' }),
          itemType: 'portfolio',
          params: {
            portfolio_id,
          },
          endpoint: '/citec/portfolio',
        },
        /* istanbul ignore next */
        isDeleted => {
          /* istanbul ignore next */
          if (isDeleted) dispatch(removeSavedPortfolio(portfolio_id));
        },
      );
    },
    [dispatch, deleteItemPopup, t],
  );

  const handleCloseModalCash = useCallback(() => {
    setOpenModalCash(false);
  }, []);

  const onOpenPortfolioModal = useCallback(() => {
    setIsEditPortfolio(false);
    setOpenModalPortfolio(true);
  }, []);

  const onClosePortfolioModal = useCallback(() => {
    setOpenModalPortfolio(false);
    setEditPortfolioConfig(initialEditPortfolioConfig);
  }, [initialEditPortfolioConfig]);

  return {
    portfolioName,
    dataIdeasTable,
    dataPortfolio,
    openModalCash,
    loadIdeaOnRedux,
    handleEditPortfolio,
    editPortfolioConfig,
    handleDeleteIdea,
    handleCloseModalCash,
    onOpenPortfolioModal,
    openModalPortfolio,
    onClosePortfolioModal,
    workflow,
    isEditPortfolio,
  };
};
